import type { ReactNode, AnchorHTMLAttributes } from 'react'
import { cn } from '../../utils/cn'

export interface ResponsiveNavItem {
	href: string
	label: string
	icon?: ReactNode
	active?: boolean
}
export interface ResponsiveNavProps {
	items: ResponsiveNavItem[]
	label: string
	className?: string
	renderLink?: (
		props: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string },
	) => ReactNode
}

/** One set of destinations: inline on desktop, a safe-area-aware bottom bar on mobile. */
export function ResponsiveNav({
	items,
	label,
	className,
	renderLink,
}: ResponsiveNavProps) {
	return (
		<nav
			aria-label={label}
			className={cn(
				'fixed inset-x-0 bottom-0 z-40 flex justify-around border-t border-navy/15 bg-white text-navy pb-[env(safe-area-inset-bottom)] lg:static lg:justify-start lg:gap-5 lg:border-0 lg:bg-transparent lg:text-inherit lg:pb-0',
				className,
			)}
		>
			{items.map((item) => {
				const props = {
					href: item.href,
					'aria-current': item.active ? ('page' as const) : undefined,
					className: cn(
						'relative flex min-h-16 min-w-0 flex-1 flex-col items-center justify-center gap-1 px-2 py-2 font-ui text-xs focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold lg:min-h-16 lg:flex-none lg:flex-row lg:text-sm',
						item.active &&
							'font-semibold after:absolute after:inset-x-2 after:top-0 after:h-0.5 after:bg-gold lg:after:bottom-2 lg:after:top-auto',
					),
					children: (
						<>
							<span aria-hidden="true" className="lg:hidden">
								{item.icon}
							</span>
							<span>{item.label}</span>
						</>
					),
				}
				return (
					<span key={item.href} className="contents">
						{renderLink ? renderLink(props) : <a {...props} />}
					</span>
				)
			})}
		</nav>
	)
}
